import React, { useState } from 'react';
import {
  Box, Card, Typography, Button, TextField, MenuItem, Select, FormControl, InputLabel, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer, Alert, CircularProgress, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import axios from 'axios';
import useSWR from 'swr';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSelector } from 'react-redux';
import { apiHost } from '../utils/apiHost';
import { waktuCabang } from '../utils/waktuCabang';
import { exportStyledExcel, pdfTableTheme } from '../utils/exportHelpers';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};
const fetcher = (url) => axios.get(url, { withCredentials: true }).then((res) => res.data);
const rp = (n) => {
  const v = Math.round(Number(n) || 0);
  return `${v < 0 ? '-' : ''}Rp ${Math.abs(v).toLocaleString('id-ID')}`;
};
const persen = (v) => `${(Number(v) || 0).toLocaleString('id-ID', { maximumFractionDigits: 1 })}%`;

/** Laporan laba rugi: per cabang (satu bulan) atau per bulan (satu tahun), dengan ekspor Excel/PDF. */
export const LabaRugi = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const kini = waktuCabang(new Date());
  const [mode, setMode] = useState(superadmin ? 'cabang' : 'bulan');
  const [bulan, setBulan] = useState(`${kini.getFullYear()}-${String(kini.getMonth() + 1).padStart(2, '0')}`);
  const [tahun, setTahun] = useState(String(kini.getFullYear()));
  const [cabang, setCabang] = useState('');

  const url = user
    ? `${getApiBaseUrl()}/laporan/laba-rugi?mode=${mode}&${mode === 'cabang' ? `bulan=${bulan}` : `tahun=${tahun}`}${superadmin && cabang ? `&cabangUuid=${cabang}` : ''}`
    : null;
  const { data, error, isLoading } = useSWR(url, fetcher, { keepPreviousData: true });
  const { data: cabangList } = useSWR(superadmin ? `${getApiBaseUrl()}/cabang` : null, fetcher);

  const kat = data?.kategoriPengeluaran || [];
  const judulKolom = mode === 'cabang' ? 'Cabang' : 'Bulan';
  const headers = [judulKolom, 'Penjualan', 'HPP (modal)', 'Laba kotor', ...kat, 'Total pengeluaran', 'Gaji', 'Laba bersih', 'Margin'];
  const nilaiBaris = (b) => [b.penjualan, b.hpp, b.labaKotor, ...kat.map((k) => b.pengeluaran[k] || 0), b.totalPengeluaran, b.gaji, b.labaBersih];
  const namaCabang = superadmin
    ? (cabangList?.data?.find((c) => c.uuid === cabang)?.namacabang || 'Semua cabang')
    : (data?.cabang?.nama || user?.cabang?.namacabang || '-');
  const namaBerkas = `Laba_Rugi_${mode === 'cabang' ? bulan : tahun}${cabang ? `_${namaCabang.replace(/\W+/g, '_')}` : ''}`;
  const meta = [['Periode', data?.periode || '-'], ['Cabang', namaCabang], ['Catatan', 'Penjualan sebelum PPN; HPP dari harga modal produk (modal 0 dihitung 0)']];

  const eksporExcel = async () => {
    await exportStyledExcel({
      fileName: `${namaBerkas}.xlsx`,
      sheetName: 'Laba Rugi',
      title: 'Laporan Laba Rugi',
      metaRows: meta,
      headers,
      rows: data.baris.map((b) => [b.label, ...nilaiBaris(b), persen(b.margin)]),
      columnWidths: [22, ...headers.slice(1).map(() => 16)],
      grandTotalRow: { label: 'TOTAL', values: [...nilaiBaris(data.total), persen(data.total.margin)] },
    });
  };

  const eksporPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    const lebar = doc.internal.pageSize.getWidth();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Laporan Laba Rugi', lebar / 2, 16, { align: 'center' });
    doc.setFontSize(10);
    meta.forEach(([a], i) => doc.text(a, 14, 26 + i * 6));
    doc.setFont('helvetica', 'normal');
    meta.forEach(([, b], i) => doc.text(b, 44, 26 + i * 6));
    doc.autoTable({
      startY: 26 + meta.length * 6,
      head: [headers],
      body: data.baris.map((b) => [b.label, ...nilaiBaris(b).map(rp), persen(b.margin)]),
      foot: [['TOTAL', ...nilaiBaris(data.total).map(rp), persen(data.total.margin)]],
      showFoot: 'lastPage',
      ...pdfTableTheme,
      styles: { ...pdfTableTheme.styles, fontSize: kat.length > 4 ? 7 : 8, halign: 'center', lineColor: [189, 189, 189], lineWidth: 0.2 },
      headStyles: { ...pdfTableTheme.headStyles, halign: 'center' },
      footStyles: { fillColor: [255, 213, 79], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
    });
    doc.save(`${namaBerkas}.pdf`);
  };

  const sel = { whiteSpace: 'nowrap' };
  const angkaSel = (v) => <TableCell align="right" sx={{ ...sel, color: v < 0 ? 'error.main' : 'inherit' }}>{rp(v)}</TableCell>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Laba Rugi</Typography>
          <Typography variant="body2" color="text.secondary">
            Penjualan, HPP, laba kotor, pengeluaran, gaji, dan laba bersih — untuk tutup buku bulanan.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)} sx={{ bgcolor: 'white' }}>
            <ToggleButton value="cabang">Per cabang</ToggleButton>
            <ToggleButton value="bulan">Per bulan</ToggleButton>
          </ToggleButtonGroup>
          {mode === 'cabang' ? (
            <TextField label="Bulan" type="month" size="small" value={bulan} onChange={(e) => e.target.value && setBulan(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white', width: 170 }} />
          ) : (
            <TextField label="Tahun" type="number" size="small" value={tahun} onChange={(e) => /^\d{0,4}$/.test(e.target.value) && setTahun(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white', width: 110 }} />
          )}
          {superadmin && (
            <FormControl size="small" sx={{ minWidth: 190, bgcolor: 'white' }}>
              <InputLabel shrink>Cabang</InputLabel>
              <Select value={cabang} label="Cabang" notched displayEmpty
                renderValue={(v) => (v ? cabangList?.data?.find((c) => c.uuid === v)?.namacabang || '-' : 'Semua cabang')}
                onChange={(e) => setCabang(e.target.value)}>
                <MenuItem value="">Semua cabang</MenuItem>
                {cabangList?.data?.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Button variant="contained" color="primary" onClick={eksporPdf} disabled={!data?.baris}>Export PDF</Button>
          <Button variant="contained" color="success" onClick={eksporExcel} disabled={!data?.baris}>Export Excel</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.response?.data?.message || error.message}</Alert>}

      {data && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          {[
            ['Penjualan', data.total.penjualan],
            ['Laba kotor', data.total.labaKotor],
            ['Pengeluaran + gaji', data.total.totalPengeluaran + data.total.gaji],
            ['Laba bersih', data.total.labaBersih],
          ].map(([l, v]) => (
            <Card key={l} sx={{ px: 2, py: 1.5, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', minWidth: 170 }}>
              <Typography variant="body2" color="text.secondary">{l}</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: v < 0 ? 'error.main' : 'inherit' }}>{rp(v)}</Typography>
            </Card>
          ))}
          <Card sx={{ px: 2, py: 1.5, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', minWidth: 120 }}>
            <Typography variant="body2" color="text.secondary">Margin bersih</Typography>
            <Typography variant="h6" fontWeight={700}>{persen(data.total.margin)}</Typography>
          </Card>
        </Box>
      )}

      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#3F51B5', color: 'white', fontWeight: 700, ...sel } }}>
                {headers.map((h, i) => <TableCell key={h} align={i === 0 ? 'left' : 'right'}>{h}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && !data && (
                <TableRow><TableCell colSpan={headers.length} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {data?.baris.map((b, i) => (
                <TableRow key={b.kunci} sx={{ bgcolor: i % 2 ? '#F5F5F5' : 'white' }}>
                  <TableCell sx={{ fontWeight: 600, ...sel }}>{b.label}</TableCell>
                  {nilaiBaris(b).map((v, j) => <React.Fragment key={j}>{angkaSel(v)}</React.Fragment>)}
                  <TableCell align="right" sx={sel}>{persen(b.margin)}</TableCell>
                </TableRow>
              ))}
              {data && (
                <TableRow sx={{ bgcolor: '#FFD54F', '& td': { fontWeight: 700 } }}>
                  <TableCell>TOTAL</TableCell>
                  {nilaiBaris(data.total).map((v, j) => <React.Fragment key={j}>{angkaSel(v)}</React.Fragment>)}
                  <TableCell align="right" sx={sel}>{persen(data.total.margin)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Penjualan = nilai item terjual sebelum PPN. HPP = jumlah × harga modal (produk dengan harga modal 0 dihitung 0).
        Pengeluaran dari menu Pengeluaran; gaji dari menu Gaji Karyawan.
      </Typography>
    </Box>
  );
};
